/** File-kind helpers for CFOS browse / search / upload lists. */

export function fileExtension(file: { filename: string } | string): string {
  const name = typeof file === 'string' ? file : file.filename
  const base = name.split(/[/\\]/).pop() || name
  const dot = base.lastIndexOf('.')
  if (dot <= 0 || dot === base.length - 1) return ''
  return base.slice(dot + 1).toLowerCase()
}

/** True when the display name ends with .pdf (case-insensitive). */
export function isPdfFile(file: { filename: string } | string): boolean {
  return fileExtension(file) === 'pdf'
}

/** Word/Excel/PowerPoint formats editable in Microsoft 365 for the web. */
const OFFICE_WEB_EXTS = new Set(['docx', 'xlsx', 'pptx'])

export function isOfficeWebFile(file: { filename: string } | string): boolean {
  return OFFICE_WEB_EXTS.has(fileExtension(file))
}

/** Soft client-side gate matching GRAPH_OFFICE_MAX_BYTES default (100 MB). */
export const OFFICE_WEB_MAX_BYTES = 100 * 1024 * 1024

export function isOfficeWebEditableSize(file: { file_size: number }): boolean {
  return file.file_size <= OFFICE_WEB_MAX_BYTES
}

export type CfosFileKind =
  | 'pdf'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'other'

const WORD_EXTS = new Set(['doc', 'docx', 'docm', 'dot', 'dotx', 'odt', 'rtf'])
const EXCEL_EXTS = new Set(['xls', 'xlsx', 'xlsm', 'xlsb', 'xlt', 'xltx', 'csv', 'ods'])
const POWERPOINT_EXTS = new Set(['ppt', 'pptx', 'pptm', 'pot', 'potx', 'odp'])

export function getFileKind(file: { filename: string } | string): CfosFileKind {
  const ext = fileExtension(file)
  if (ext === 'pdf') return 'pdf'
  if (WORD_EXTS.has(ext)) return 'word'
  if (EXCEL_EXTS.has(ext)) return 'excel'
  if (POWERPOINT_EXTS.has(ext)) return 'powerpoint'
  return 'other'
}

export function officeAppLabel(file: { filename: string } | string): string {
  switch (getFileKind(file)) {
    case 'word':
      return 'Word'
    case 'excel':
      return 'Excel'
    case 'powerpoint':
      return 'PowerPoint'
    default:
      return 'Microsoft 365'
  }
}
